import { Injectable } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { AssignmentForm } from "../modals/assignment-modal.component";
import { AssignmentSolutionData, ManifestAssignment, MobaroAsset } from "../assignment.interface";
import { AssignmentTrigger, AssignmentWithData, ImageAttachment } from "../../checklists/checklist.interface";
import { formatISO } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

@Injectable({ providedIn: "root" })
export class AssignmentFormService {
	public createAssignmentForm = (
		assignment: ManifestAssignment,
		solution: AssignmentSolutionData,
	): FormGroup<AssignmentForm> => {
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const startTimeForPicker =
			assignment.start == null ? null : formatISO(toZonedTime(assignment.start, userTimeZone));
		const endTimeForPicker = assignment.end == null ? null : formatISO(toZonedTime(assignment.end, userTimeZone));

		const assisgnmentForm = new FormGroup<AssignmentForm>(
			{
				title: new FormControl<string>({ value: assignment.name, disabled: false }, [Validators.required]),
				attachments: new FormControl<ImageAttachment[]>({
					value: assignment.attachments,
					disabled: false,
				}),
				description: new FormControl<string>({ value: assignment.description, disabled: false }),
				definitionState: new FormControl<DocumentReference>({
					value: assignment.definitionState,
					disabled: false,
				}),
				definitionPriority: new FormControl<DocumentReference>({
					value: assignment.definitionPriority,
					disabled: false,
				}),
				priority: new FormControl<boolean>({ value: assignment.priority, disabled: false }),
				asset: new FormControl<MobaroAsset>({ value: assignment.asset, disabled: false }),
				assignees: new FormControl<DocumentReference[]>({ value: assignment.assignees, disabled: false }, [
					Validators.required,
				]),
				categories: new FormControl<string[]>({ value: assignment.categories, disabled: false }),
				definitionCategories: new FormControl<string[]>({
					value: assignment.definitionCategories,
					disabled: false,
				}),
				start: new FormControl<string>({ value: startTimeForPicker, disabled: false }),
				end: new FormControl<string>({ value: endTimeForPicker, disabled: false }),
				solution: new FormControl<AssignmentSolutionData>({ value: solution, disabled: false }),
			},
			[this.createDateRangeValidator()],
		);

		return assisgnmentForm;
	};

	public AssignmentFromForm = (
		assignment: ManifestAssignment,
		assignmentForm: FormGroup<AssignmentForm>,
		trigger: AssignmentTrigger,
	): AssignmentWithData => {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		const assignmentWithData: AssignmentWithData = {
			assignment: {
				...assignment,
				attachments: assignmentForm.controls.attachments.value,
				name: assignmentForm.controls.title.value,
				description: assignmentForm.controls.description.value,
				definitionState: assignmentForm.controls.definitionState.value,
				definitionPriority: assignmentForm.controls.definitionPriority.value,
				priority: assignmentForm.controls.priority.value,
				asset: assignmentForm.controls.asset.value,
				assignees: assignmentForm.controls.assignees.value,
				categories: assignmentForm.controls.categories.value,
				definitionCategories: assignmentForm.controls.definitionCategories.value,
				start: this.getUTCISOStringFromLocalDateTimeString(assignmentForm.controls.start.value, timeZone),
				end: this.getUTCISOStringFromLocalDateTimeString(assignmentForm.controls.end.value, timeZone),
			},
			solution: assignmentForm.controls.solution.value,
			triggeredBy: trigger,
		};

		return assignmentWithData;
	};

	private getUTCISOStringFromLocalDateTimeString = (dateTimeString: string, timeZone: string) => {
		if (dateTimeString == null) {
			return null;
		}
		return fromZonedTime(dateTimeString, timeZone).toISOString();
	};

	private createDateRangeValidator(): ValidatorFn {
		return (form: AbstractControl): ValidationErrors | null => {
			const start: string = form.get("start").value;
			const end: string = form.get("end").value;

			if (start && end) {
				const isRangeValid = new Date(end).getTime() - new Date(start).getTime() > 0;

				return isRangeValid ? null : { dateRange: true };
			}

			return null;
		};
	}
}
